import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import CoinCard from "../components/CoinCard";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1673904788072-40f58f0c43e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxyYXJlJTIwZ29sZCUyMGNvaW4lMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODE3Mzc2MDF8MA&ixlib=rb-4.1.0&q=85";

const Home = () => {
  const [coins, setCoins] = useState([]);
  useEffect(() => {
    api.get("/coins").then(({ data }) => setCoins(data)).catch(() => setCoins([]));
  }, []);

  const featured = coins.filter((c) => c.featured && !c.sold).slice(0, 5);
  const recent = coins.filter((c) => !c.featured).slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Gold coins" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-24 max-w-5xl fade-up">
          <div className="coin-tag text-[#D4AF37] mb-6 fade-up fade-up-delay-1">Est. MMXXVI · Private Gallery</div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] mb-8 fade-up fade-up-delay-2">
            Where history<br />
            <span className="gold-shimmer italic">finds its weight.</span>
          </h1>
          <p className="text-lg text-[#A19D94] max-w-xl mb-10 fade-up fade-up-delay-3">
            A curated collection of rare numismatic objects — gold and silver, acquired from auctions and private collectors.
          </p>
          <div className="flex flex-wrap gap-4 fade-up fade-up-delay-4">
            <Link to="/catalog" className="bg-[#D4AF37] text-black font-medium px-8 py-4 hover:bg-[#F1C40F] transition-colors inline-flex items-center gap-3" data-testid="hero-catalog-btn">
              View Catalog <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* MARQUEE STATS */}
      <section className="border-y border-[#F3EFE0]/10 px-6 md:px-12 lg:px-24 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          ["Coins in Catalog", coins.length],
          ["Years in Business", "3"],
          ["Authenticity", "100%"],
        ].map(([label, value]) => (
          <div key={label}>
            <div className="font-display text-3xl md:text-4xl text-[#D4AF37]">{value}</div>
            <div className="coin-tag mt-1">{label}</div>
          </div>
        ))}
      </section>

      {/* FEATURED CATALOG */}
      {featured.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="featured-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="coin-tag mb-3">Curator&apos;s Selection</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Featured Specimens</h2>
            </div>
            <Link to="/catalog" className="coin-tag text-[#A19D94] hover:text-[#D4AF37] flex items-center gap-2">
              See all <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {featured.map((coin, i) => (
              <CoinCard
                key={coin.id}
                coin={coin}
                span={i === 0 ? "col-span-1 md:col-span-8 row-span-2" : "col-span-1 md:col-span-4"}
              />
            ))}
          </div>
        </section>
      )}

      {/* RECENT ACQUISITIONS */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-[#0A0A0A] border-t border-[#F3EFE0]/5">
        <div className="mb-12">
          <div className="coin-tag mb-3">Recent Acquisitions</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">From the Vault</h2>
        </div>
        {recent.length === 0 ? (
          <div className="border border-dashed border-[#F3EFE0]/10 p-16 text-center">
            <p className="font-display text-2xl text-[#A19D94]">The vault awaits its first acquisition.</p>
            <p className="coin-tag mt-4">Admin: log in to add coins</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {recent.map((coin) => (
              <CoinCard key={coin.id} coin={coin} span="col-span-1 md:col-span-4" />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
